// RelativeTimeFormat construction is non-trivial; cache one per locale.
const relativeTimeFormatterCache = new Map<string, Intl.RelativeTimeFormat | null>();

function getRelativeTimeFormatter(locale: string): Intl.RelativeTimeFormat | null {
  if (relativeTimeFormatterCache.has(locale)) {
    return relativeTimeFormatterCache.get(locale) ?? null;
  }
  if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') {
    relativeTimeFormatterCache.set(locale, null);
    return null;
  }
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    relativeTimeFormatterCache.set(locale, rtf);
    return rtf;
  } catch {
    relativeTimeFormatterCache.set(locale, null);
    return null;
  }
}

/**
 * Format a date as a short relative time string ("3m ago", "2h ago", "Just now").
 *
 * Uses Intl.RelativeTimeFormat when available; falls back to plain English.
 *
 * @param date Source date (string or Date instance)
 * @param locale Optional BCP-47 locale tag. Defaults to "en".
 */
export function relativeTime(date: string | Date, locale: string = 'en'): string {
  const target = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(target.getTime())) {
    return '';
  }

  const diffMs = target.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);

  // Treat anything under a minute as "Just now" so the 45-59s window
  // doesn't fall through to the non-localized fallback at the end.
  if (absSec < 60) {
    const rtf = getRelativeTimeFormatter(locale);
    if (rtf) {
      return rtf.format(0, 'second');
    }
    return 'Just now';
  }

  const units: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number; short: string }> = [
    { unit: 'year', seconds: 31_536_000, short: 'y' },
    { unit: 'month', seconds: 2_592_000, short: 'mo' },
    { unit: 'week', seconds: 604_800, short: 'w' },
    { unit: 'day', seconds: 86_400, short: 'd' },
    { unit: 'hour', seconds: 3_600, short: 'h' },
    { unit: 'minute', seconds: 60, short: 'm' },
  ];

  for (const { unit, seconds, short } of units) {
    if (absSec >= seconds) {
      const value = Math.round(diffSec / seconds);
      const rtf = getRelativeTimeFormatter(locale);
      if (rtf) {
        return rtf.format(value, unit);
      }
      const absVal = Math.abs(value);
      return value < 0 ? `${absVal}${short} ago` : `in ${absVal}${short}`;
    }
  }

  return 'Just now';
}

/**
 * Format a date as a full absolute timestamp using the given locale.
 */
export function absoluteTime(date: string | Date, locale: string = 'en'): string {
  const target = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(target.getTime())) {
    return '';
  }
  try {
    return target.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return target.toLocaleString();
  }
}
